import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { Plus, Trash2, Clock, ShieldAlert, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { orePersonaleApi, tecniciApi } from '../services/api'

const MESI = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic']

type FiltroScadenze = 'tutte' | 'in_scadenza' | 'scadute'

function ScadenzeCertificazioni() {
  const [filtro, setFiltro] = useState<FiltroScadenze>('tutte')
  const [giorni, setGiorni] = useState(30)

  const { data: allarmi = [], isLoading } = useQuery({
    queryKey: ['scadenze-allarme', giorni],
    queryFn: () => tecniciApi.scadenzeAllarme(giorni).then(r => r.data),
  })

  const filtered = (allarmi as any[]).filter(a => {
    if (filtro === 'scadute') return a.giorni_rimanenti < 0
    if (filtro === 'in_scadenza') return a.giorni_rimanenti >= 0
    return true
  })

  const scaduteCount = (allarmi as any[]).filter(a => a.giorni_rimanenti < 0).length
  const inScadenzaCount = (allarmi as any[]).filter(a => a.giorni_rimanenti >= 0).length

  const statoChip = (giorni: number) => {
    if (giorni < 0) return { label: `Scaduta ${Math.abs(giorni)}gg fa`, bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' }
    if (giorni <= 7) return { label: `${giorni}gg`, bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' }
    if (giorni <= 30) return { label: `${giorni}gg`, bg: '#fef9c3', color: '#92400e', border: '#fde68a' }
    return { label: `${giorni}gg`, bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' }
  }

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['tutte', 'in_scadenza', 'scadute'] as FiltroScadenze[]).map(f => {
            const labels: Record<FiltroScadenze, string> = {
              tutte: `Tutte (${(allarmi as any[]).length})`,
              in_scadenza: `In scadenza (${inScadenzaCount})`,
              scadute: `Scadute (${scaduteCount})`,
            }
            return (
              <button
                key={f}
                onClick={() => setFiltro(f)}
                style={{
                  padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                  background: filtro === f ? (f === 'scadute' ? '#fee2e2' : f === 'in_scadenza' ? '#fef9c3' : '#eff6ff') : '#f8fafc',
                  color: filtro === f ? (f === 'scadute' ? '#dc2626' : f === 'in_scadenza' ? '#92400e' : '#1E40AF') : '#64748b',
                  borderColor: filtro === f ? (f === 'scadute' ? '#fca5a5' : f === 'in_scadenza' ? '#fde68a' : '#bfdbfe') : '#e2e8f0',
                }}
              >
                {labels[f]}
              </button>
            )
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
          Soglia:
          <select className="select" style={{ padding: '2px 8px', fontSize: 12 }} value={giorni} onChange={e => setGiorni(Number(e.target.value))}>
            <option value={30}>30 giorni</option>
            <option value={60}>60 giorni</option>
            <option value={90}>90 giorni</option>
          </select>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#F8FAFC' }}>
            <tr>
              {['Tecnico', 'Documento', 'Scadenza', 'Stato', 'Azioni'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#64748B', fontWeight: 600, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>Caricamento…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }}>
                {filtro === 'tutte' ? 'Nessuna scadenza nei prossimi ' + giorni + ' giorni' : 'Nessun elemento per il filtro selezionato'}
              </td></tr>
            ) : filtered.map((a: any, i: number) => {
              const chip = statoChip(a.giorni_rimanenti)
              return (
                <tr key={i} style={{ borderBottom: '1px solid #F3F4F6', background: a.giorni_rimanenti < 0 ? '#fff5f5' : undefined }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{a.tecnico_nome}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{a.tipo}</td>
                  <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12 }}>
                    {a.scadenza ? new Date(a.scadenza).toLocaleDateString('it-IT') : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}>
                      {chip.label}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <Link
                      to={`/utenti`}
                      title="Vai a Utenti per aggiornare il documento del tecnico"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#1E40AF', textDecoration: 'none' }}
                    >
                      <ExternalLink size={12} /> Aggiorna
                    </Link>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function GestionePersonale() {
  const qc = useQueryClient()
  const now = new Date()
  const [tab, setTab] = useState<'ore' | 'scadenze'>('ore')
  const [anno, setAnno] = useState(now.getFullYear())
  const [mese, setMese] = useState(now.getMonth() + 1)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<any>(null)
  const [form, setForm] = useState({ tecnico_id: '', data: '', ora_inizio: '', ora_fine: '', note: '' })

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-gp'],
    queryFn: () => tecniciApi.list({ is_active: true }).then(r => r.data.items ?? r.data),
  })
  const tecnici: any[] = Array.isArray(tecniciData) ? tecniciData : (tecniciData?.items ?? [])

  const { data: ore = [], isLoading } = useQuery({
    queryKey: ['ore-personale', anno, mese],
    queryFn: () => orePersonaleApi.list({ anno, mese }).then(r => r.data),
  })

  const { data: riepilogo } = useQuery({
    queryKey: ['ore-riepilogo', anno, mese],
    queryFn: () => orePersonaleApi.riepilogo(anno, mese).then(r => r.data),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ore-personale', anno, mese] })
    qc.invalidateQueries({ queryKey: ['ore-riepilogo', anno, mese] })
  }

  const saveMut = useMutation({
    mutationFn: (d: any) => editing
      ? orePersonaleApi.update(editing.id, d)
      : orePersonaleApi.create(d),
    onSuccess: () => { toast.success('Salvato'); invalidate(); setShowForm(false); setEditing(null) },
    onError: () => toast.error('Errore salvataggio'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => orePersonaleApi.remove(id),
    onSuccess: () => { toast.success('Eliminato'); invalidate() },
    onError: () => toast.error('Errore eliminazione'),
  })

  const openNew = () => {
    setEditing(null)
    setForm({ tecnico_id: '', data: '', ora_inizio: '', ora_fine: '', note: '' })
    setShowForm(true)
  }

  const openEdit = (o: any) => {
    setEditing(o)
    setForm({
      tecnico_id: String(o.tecnico_id),
      data: o.data,
      ora_inizio: o.ora_inizio,
      ora_fine: o.ora_fine,
      note: o.note ?? '',
    })
    setShowForm(true)
  }

  const handleSave = () => {
    if (!form.tecnico_id || !form.data || !form.ora_inizio || !form.ora_fine) {
      toast.error('Compila tutti i campi obbligatori')
      return
    }
    saveMut.mutate({ ...form, tecnico_id: Number(form.tecnico_id) })
  }

  return (
    <div className="space-y-6">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 8 }}>
          {tab === 'ore' ? <Clock size={20} color="#1E40AF" /> : <ShieldAlert size={20} color="#dc2626" />}
          Gestione Personale — {tab === 'ore' ? 'Ore Lavoro' : 'Scadenze Certificazioni'}
        </h1>
        {tab === 'ore' && (
          <button className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={openNew}>
            <Plus size={15} /> Aggiungi
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #E5E7EB', paddingBottom: 0 }}>
        {([['ore', 'Ore Lavoro'], ['scadenze', 'Scadenze Certificazioni']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none', borderBottom: tab === key ? '2px solid #1E40AF' : '2px solid transparent',
              background: 'transparent', color: tab === key ? '#1E40AF' : '#64748b', marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'ore' && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <select className="select" value={mese} onChange={e => setMese(Number(e.target.value))}>
          {MESI.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select className="select" value={anno} onChange={e => setAnno(Number(e.target.value))}>
          {[2024,2025,2026,2027].map(a => <option key={a}>{a}</option>)}
        </select>
      </div>}

      {tab === 'ore' && riepilogo && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Ore totali</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#1E40AF' }}>{(riepilogo.totale_ore ?? 0).toFixed(1)} h</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Costo totale</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>€ {(riepilogo.totale_costo ?? 0).toFixed(2)}</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>Tecnici con ore</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#374151' }}>{riepilogo.tecnici?.length ?? 0}</div>
          </div>
        </div>
      )}

      {tab === 'ore' && riepilogo?.tecnici?.length > 0 && (
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Riepilogo per tecnico</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                {['Tecnico','Ore','€/h','Costo ore'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 10px', color: '#64748B', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {riepilogo.tecnici.map((r: any) => (
                <tr key={r.tecnico_id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{r.tecnico_nome}</td>
                  <td style={{ padding: '8px 10px' }}>{r.ore_totali.toFixed(1)}</td>
                  <td style={{ padding: '8px 10px' }}>€ {r.costo_orario.toFixed(2)}</td>
                  <td style={{ padding: '8px 10px', color: '#15803d', fontWeight: 600 }}>€ {r.costo_totale.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'ore' && <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#F8FAFC' }}>
            <tr>
              {['Data','Tecnico','Dalle','Alle','Ore','Note',''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#64748B', fontWeight: 600, borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>Caricamento…</td></tr>
            ) : (ore as any[]).length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#94A3B8' }}>Nessuna voce per questo periodo</td></tr>
            ) : (ore as any[]).map((o: any) => (
              <tr key={o.id} style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }} onClick={() => openEdit(o)}>
                <td style={{ padding: '10px 14px' }}>{o.data}</td>
                <td style={{ padding: '10px 14px', fontWeight: 500 }}>{o.tecnico_nome}</td>
                <td style={{ padding: '10px 14px' }}>{o.ora_inizio}</td>
                <td style={{ padding: '10px 14px' }}>{o.ora_fine}</td>
                <td style={{ padding: '10px 14px', color: '#1E40AF', fontWeight: 600 }}>{o.ore_totali?.toFixed(1)}</td>
                <td style={{ padding: '10px 14px', color: '#64748B' }}>{o.note}</td>
                <td style={{ padding: '10px 14px' }}>
                  <button className="btn-ghost" style={{ padding: '4px 8px' }} onClick={e => { e.stopPropagation(); deleteMut.mutate(o.id) }}>
                    <Trash2 size={14} color="#dc2626" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>{editing ? 'Modifica voce' : 'Nuova voce ore'}</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Tecnico *</label>
                <select className="select" value={form.tecnico_id} onChange={e => setForm(f => ({ ...f, tecnico_id: e.target.value }))}>
                  <option value="">Seleziona tecnico…</option>
                  {tecnici.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Data *</label>
                <input type="date" className="input" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="label">Dalle *</label>
                  <input type="time" className="input" value={form.ora_inizio} onChange={e => setForm(f => ({ ...f, ora_inizio: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Alle *</label>
                  <input type="time" className="input" value={form.ora_fine} onChange={e => setForm(f => ({ ...f, ora_fine: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label">Note</label>
                <input type="text" className="input" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => { setShowForm(false); setEditing(null) }}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saveMut.isPending}>
                {saveMut.isPending ? 'Salvo…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
